import { useCallback, useEffect, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';

import {
  isInvalidRequestAccessStatus,
  isInvalidSessionStatus,
  isSession,
} from '../utilities';
import { getCurrentSessionStatus } from '../utilities/api/getDataFromApi';
import { MAX_ERROR_COUNT, REFETCH_INTERVAL } from '../utilities/constants';

/**
 * Custom hook for handling periodic refetching of session status
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isListRowApiSuccess - Flag indicating if the initial API call was successful
 * @param {Array} params.paginatedEntities - Array of paginated entities containing session data
 * @param {Function} params.setPaginatedEntities - Function to update paginated entities
 * @param {Function} params.onSidePanelClose - Function to close the sidepanel when filtering occurs
 * @param {Object} params.currentListItem - Currently selected item in the sidepanel
 * @returns {void}
 */
const useSessionStatusRefetch = ({
  isListRowApiSuccess,
  paginatedEntities,
  setPaginatedEntities,
  onSidePanelClose,
  currentListItem,
}) => {
  const [errorCount, setErrorCount] = useState(0);
  const timeoutIdRef = useRef(null);
  const isPollingActiveRef = useRef(false);

  /**
   * Clears the current timeout if it exists
   */
  const clearCurrentTimeout = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  /**
   * Filters and returns sessions from paginated entities
   * @returns {Array} Array of session entities
   */
  const getSessions = useCallback(
    () => paginatedEntities?.filter((entity) => isSession(entity)),
    [paginatedEntities, isSession]
  );

  /**
   * Updates entity status based on API response
   * @param {Array} prevEntities - Previous entities array
   * @param {Object} response - API response object
   * @returns {Array} Updated entities array
   */
  const updateEntitiesStatus = useCallback(
    (prevEntities, response) => {
      if (!prevEntities || !response?.result?.sessions) return prevEntities;

      const sessionMap = new Map(
        response.result.sessions.map((session) => [
          session.requestAccessKey,
          session,
        ])
      );

      return prevEntities
        .filter((entity) => {
          if (!isSession(entity)) return true;

          const updatedStatus = sessionMap.get(entity.requestAccessKey);

          if (!updatedStatus) return true;

          const shouldRemove =
            isInvalidSessionStatus(updatedStatus.sessionStatus) ||
            isInvalidRequestAccessStatus(updatedStatus.requestAccessStatus);

          if (shouldRemove) {
            if (currentListItem?.requestAccessKey === entity.requestAccessKey) {
              onSidePanelClose();
            }

            return false;
          }

          return true;
        })
        .map((entity) => {
          if (!isSession(entity)) return entity;

          const updatedStatus = sessionMap.get(entity.requestAccessKey);

          if (!updatedStatus) return entity;

          // Return a new object reference so React.memo / useMemo consumers
          // detect the change (Object.assign would mutate in-place and leave
          // stale references, causing gatewayStates updates to be missed).
          return {
            ...entity,
            sessionStatus: updatedStatus.sessionStatus,
            requestAccessStatus: updatedStatus.requestAccessStatus,
            ...(updatedStatus.gatewayStates !== undefined && {
              gatewayStates: updatedStatus.gatewayStates,
            }),
          };
        });
    },
    [isSession, currentListItem]
  );

  /**
   * Handles API errors and updates error count
   * @param {Error} error - Error object from API call
   */
  const handleApiError = (error) => {
    setErrorCount((prevCount) => {
      const newCount = prevCount + 1;

      if (newCount >= MAX_ERROR_COUNT) {
        Logger.error('Session status polling error:', error);
        clearCurrentTimeout();
        isPollingActiveRef.current = false;
      }

      return newCount;
    });
  };

  /**
   * Makes API call to get current session status
   * @param {Array} payload - Array of request access keys
   */
  const makeApiCall = useCallback(
    async (payload) => {
      try {
        const response = await getCurrentSessionStatus(payload);

        if (response?.result) {
          setPaginatedEntities((prevEntities) =>
            updateEntitiesStatus(prevEntities, response)
          );
        } else {
          handleApiError(response);
        }
      } catch (error) {
        handleApiError(error);
      }
    },
    [setPaginatedEntities, updateEntitiesStatus, handleApiError]
  );

  /**
   * Executes polling logic for session status updates
   */
  const executePolling = useCallback(async () => {
    clearCurrentTimeout();

    if (
      errorCount >= MAX_ERROR_COUNT ||
      !isPollingActiveRef.current ||
      document.hidden
    ) {
      return;
    }

    const currentSessions = getSessions();

    if (!currentSessions?.length) {
      isPollingActiveRef.current = false;

      return;
    }

    const payload = currentSessions.map((entity) => entity.requestAccessKey);

    await makeApiCall(payload);

    if (isPollingActiveRef.current && !document.hidden) {
      timeoutIdRef.current = setTimeout(executePolling, REFETCH_INTERVAL);
    }
  }, [errorCount, getSessions, makeApiCall]);

  /**
   * Starts the polling process
   */
  const startPolling = useCallback(() => {
    if (isPollingActiveRef.current || document.hidden) return;

    clearCurrentTimeout();

    isPollingActiveRef.current = true;
    timeoutIdRef.current = setTimeout(executePolling, REFETCH_INTERVAL);
  }, [executePolling]);

  /**
   * Stops the polling process
   */
  const stopPolling = useCallback(() => {
    isPollingActiveRef.current = false;
    clearCurrentTimeout();
  }, []);

  // Effect for handling visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearCurrentTimeout();
      } else if (isPollingActiveRef.current) {
        timeoutIdRef.current = setTimeout(executePolling, REFETCH_INTERVAL);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [executePolling]);

  // Effect for managing polling lifecycle
  useEffect(() => {
    if (!isListRowApiSuccess || errorCount >= MAX_ERROR_COUNT) {
      stopPolling();

      return;
    }

    const currentSessions = getSessions();

    if (currentSessions?.length && !isPollingActiveRef.current) {
      startPolling();
    }

    // eslint-disable-next-line consistent-return
    return stopPolling;
  }, [isListRowApiSuccess, errorCount, getSessions, startPolling, stopPolling]);
};

export default useSessionStatusRefetch;
